
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { URL } from "../../api";
import axiosInstance from "../../Request";
import { toast } from "react-toastify";

const CreateUser = () => {
  const [productList, setProductList] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProductActive, setSelectedProductActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProductCode, setNewProductCode] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    const fetchProductList = async () => {
      try {
        const res = await axiosInstance.get(URL.BRE_PRODUCT_LIST);
        setProductList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error:", err);
        toast.error("Error fetching product list. Please try again.");
      }
    };

    const fetchAllPolicies = async () => {
      try {
        const res = await axiosInstance.get(URL.BRE_POLICY_LIST);
        if (res.status === 200) {
          const updatedData = Array.isArray(res.data)
            ? res.data.map((item) => ({
                ...item,
                active: item.isActive === 1 || item.isActive === "1",
              }))
            : [];
          setRows(updatedData);
        } else {
          toast.error(`Error fetching policies: ${res.data.message || "Unknown error"}`);
        }
      } catch (err) {
        console.error("Error:", err);
        toast.error("Error fetching policies. Please try again.");
      }
    };

    fetchProductList();
    fetchAllPolicies();
  }, []);

  const handleProductSelect = async (value) => {
    if (value === "new") {
      setIsModalOpen(true);
      setSelectedProductId(value);
      setSelectedProductActive(false);
    } else {
      setSelectedProductId(value);
      const selectedProduct = productList.find((p) => p.productCode === value);
      setSelectedProductActive(selectedProduct ? selectedProduct.isActive === 1 : false);

      try {
        const url = value ? `${URL.BRE_POLICY_LIST}?product_code=${value}` : URL.BRE_POLICY_LIST;
        const res = await axiosInstance.get(url);
        if (res.status === 200) {
          const updatedRows = rows.map((row) => ({ ...row, active: false }));
          if (Array.isArray(res.data)) {
            res.data.forEach((apiItem) => {
              const index = updatedRows.findIndex((row) => row.id === apiItem.id);
              if (index !== -1) {
                updatedRows[index].active = apiItem.isActive === 1 || apiItem.isActive === "1";
              }
            });
          }
          setRows(updatedRows);
        } else {
          toast.error(`Error: ${res.data.message || "Unknown error"}`);
        }
      } catch (err) {
        console.error("Error:", err);
        toast.error("Error fetching policies for product. Please try again.");
      }
    }
  };

  const handleAddNewProductCode = async () => {
    const trimmedCode = newProductCode.trim();
    if (trimmedCode !== "") {
      try {
        const response = await axiosInstance.post(URL.BRE_ADD_PRODUCT_CODE, {
          productCode: trimmedCode,
        });

        if (response.status === 200) {
          setProductList((prev) => [
            ...prev,
            { productCode: trimmedCode, ruleId: "new", isActive: 0 },
          ]);
          setSelectedProductId(trimmedCode);
          setSelectedProductActive(false);
          setRows([]);
          setIsModalOpen(false);
          setNewProductCode("");
          toast.success(response.data.message || "New product code added successfully!");
          window.location.reload();
        } else {
          toast.error(response.data.message || "Failed to add new product code.");
        }
      } catch (err) {
        console.error("Error:", err);
        toast.error("Error adding new product code. Please try again.");
      }
    }
  };

  const updatePolicyStatus = async (id, isActive) => {
    try {
      const res = await axiosInstance.post(URL.BRE_POLICY_UPDATE, {
        id,
        productCode: selectedProductId,
        isActive: isActive ? 1 : 0,
      });

      if (res.status === 200) {
        toast.success(res.data.message || "Policy status updated successfully!");
      } else {
        toast.error(res.data.message || "Failed to update policy status.");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error updating policy status. Please try again.");
    }
  };

  const updateProductActiveStatus = async (newActiveStatus) => {
    try {
      const res = await axiosInstance.post(URL.BRE_UPDATE_PRODUCT_ACTIVE, {
        productCode: selectedProductId,
        isActive: newActiveStatus ? 1 : 0,
      });

      if (res.status === 200) {
        toast.success(res.data.message || "Product status updated");
        setProductList((prev) =>
          prev.map((p) =>
            p.productCode === selectedProductId ? { ...p, isActive: newActiveStatus ? 1 : 0 } : p
          )
        );
      } else {
        toast.error(res.data.message || "Failed to update product status");
        setSelectedProductActive(!newActiveStatus);
      }
    } catch (error) {
      toast.error("Error updating product status");
      setSelectedProductActive(!newActiveStatus);
    }
  };

  // 🟡 Handle Drag-Drop and Backend Update
  const handleDragStart = (index) => setDragIndex(index);

  const handleDrop = async (index) => {
    if (dragIndex === null || dragIndex === index) return;

    const updated = [...rows];
    const [movedRow] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, movedRow);
    setRows(updated);
    setDragIndex(null);

    const ruleIds = updated.map((row) => row.id); // Adjust if your key is `ruleId`

    try {
      const res = await axiosInstance.post(URL.BRE_POLICY_REORDER, {
        productCode: selectedProductId,
        ruleIds,
      });

      if (res.status === 200) {
        toast.success(res.data.message || "Reordered and sequences updated.");
      } else {
        toast.error(res.data.message || "Failed to update order.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to update policy sequence.");
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7" />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="border-0 mx-4">
                <Row className="align-items-center">
                  <Col xl="9">
                    <h3 className="mb-0">Create Policy</h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="mx-4">
                <Form>
                  <Row>
                    <Col md="4">
                      <FormGroup style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ flexGrow: 1 }}>
                          <label htmlFor="productSelect" className="form-control-label">
                            <u>Product Code</u>
                          </label>
                          <Input
                            type="select"
                            id="productSelect"
                            value={selectedProductId}
                            onChange={(e) => handleProductSelect(e.target.value)}
                          >
                            <option value="">Select Product</option>
                            {productList.map((product) => (
                              <option key={product.ruleId} value={product.productCode}>
                                {product.productCode}
                              </option>
                            ))}
                            <option value="new">Add New Product Code…</option>
                          </Input>
                        </div>

                        {selectedProductId && selectedProductId !== "new" && (
                          <div style={{ display: "flex", alignItems: "center", marginTop: "20px" }}>
                            <span style={{ margin: "0 10px" }}>Active:</span>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={selectedProductActive}
                                onChange={async () => {
                                  const newStatus = !selectedProductActive;
                                  setSelectedProductActive(newStatus);
                                  await updateProductActiveStatus(newStatus);
                                }}
                              />
                              <span className="slider round"></span>
                            </label>
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Table bordered striped>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Policy Name</th>
                        <th>Rule Name</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr
                          key={row.id}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDrop(index)}
                          style={{ cursor: "move" }}
                        >
                          <td>{row.id}</td>
                          <td>{row.policyName}</td>
                          <td>{row.ruleName}</td>
                          <td
                            onClick={async () => {
                              const updated = [...rows];
                              updated[index].active = !updated[index].active;
                              setRows(updated);
                              await updatePolicyStatus(row.id, updated[index].active);
                            }}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <span className={`custom-checkbox ${row.active ? "active" : ""}`}>
                              &#10004;
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)}>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          Add New Product Code
        </ModalHeader>
        <ModalBody>
          <Input
            type="text"
            placeholder="Enter new product code"
            value={newProductCode}
            onChange={(e) => setNewProductCode(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddNewProductCode}>
            Add
          </Button>
          <Button color="secondary" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CreateUser;
